// SELF — the faculties currently in play. Progressive reveal (handoff §8):
// only the tier the user is working on is foregrounded; secured lower tiers
// become quiet structure, higher tiers stay veiled.
import { useMemo } from 'react';
import { ascensionRank, GEAR_TIERS, TIER_ORDER, type GearId, type TierId } from '../olmps/ascension';
import { computeProgress, todayKey, type ProgressStats } from '../olmps/deity-progress';
import { deityLevelData } from '../olmps/deity-data';
import { useCompassConfig } from '../olmps/sectorConfig';
import { DMT_TOOLS, type DmtToolId } from '../olmps/spectrum-tools';
import { GEAR_ORDER, SEAT_BY_ID, type Seat } from './roster';

export type SeatState = 'active' | 'secured' | 'veiled';

export interface SeatView {
  seat: Seat;
  stats: ProgressStats;
  state: SeatState;
  /** The malady holding the seat while the deity isn't secured. */
  maladyName: string;
}

export interface SelfState {
  loading: boolean;
  rank: 'MORTAL' | 'DEMI' | 'TYTN' | 'OLMPN';
  securedCount: number;
  /** The tier currently being worked: the lowest tier not fully secured. */
  workingTier: TierId;
  /** The five foregrounded seats (one per gear, at the working tier). */
  active: SeatView[];
  /** Every seat with its state, for the full Mountain. */
  all: SeatView[];
  byGear: Record<GearId, SeatView>;
}

export function useSelf(userId: string | undefined): SelfState {
  const { config, loading } = useCompassConfig(userId);

  return useMemo(() => {
    const today = todayKey();
    const statsOf = (id: DmtToolId) =>
      computeProgress(config.principleProgress?.[id], deityLevelData(id), today, id);

    const statsMap = new Map<DmtToolId, ProgressStats>();
    for (const t of DMT_TOOLS) statsMap.set(t.id, statsOf(t.id));
    const securedCount = [...statsMap.values()].filter((s) => s.secured).length;

    const workingTier =
      TIER_ORDER.find((tier) => GEAR_ORDER.some((g) => !statsMap.get(GEAR_TIERS[g][tier])?.secured)) ?? 'olympian';
    const workingIdx = TIER_ORDER.indexOf(workingTier);

    const all: SeatView[] = GEAR_ORDER.flatMap((gear) =>
      TIER_ORDER.map((tier, idx) => {
        const id = GEAR_TIERS[gear][tier];
        const stats = statsMap.get(id)!;
        const tool = DMT_TOOLS.find((t) => t.id === id);
        const state: SeatState = idx === workingIdx ? 'active' : idx < workingIdx || stats.secured ? 'secured' : 'veiled';
        return { seat: SEAT_BY_ID[id], stats, state, maladyName: tool?.maladies[0]?.name ?? '' };
      }),
    );
    const active = all.filter((v) => v.state === 'active');
    const byGear = Object.fromEntries(active.map((v) => [v.seat.gear, v])) as Record<GearId, SeatView>;

    return { loading, rank: ascensionRank(securedCount), securedCount, workingTier, active, all, byGear };
  }, [config, loading]);
}
